import { DeleteOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Col, DatePicker, Form, Input, Row, Select, Space, Switch } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS, WorkSchedule } from '@tms/shared';

import { api, employeePhoto } from '@/api/client';
import { useApiMutation, useAuthedImage, useDictionaries } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { DepartmentSelect } from '@/components/DepartmentSelect';
import { PhotoCropModal } from '@/components/PhotoCropModal';
import { colorOf, initials } from '@/lib/avatar';

import { SectionHeader } from './SidePanel';
import { fullNameOf, type EmployeeDetail } from './types';

export const WORK_SCHEDULE_LABEL: Record<string, string> = {
  [WorkSchedule.FIVE_DAYS]: 'Пятидневка',
  [WorkSchedule.SHIFT]: 'Сменный график',
};

const toDate = (value: unknown): string | undefined =>
  value ? (value as dayjs.Dayjs).format('YYYY-MM-DD') : undefined;

/** Основные данные и фото. Для нового сотрудника — единственный доступный раздел. */
export function MainSection({
  employee,
  onCreated,
}: {
  employee: EmployeeDetail | null;
  onCreated: (id: number) => void;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const dictionaries = useDictionaries();
  const [form] = Form.useForm();
  const departmentId = Form.useWatch<number | undefined>('departmentId', form);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const canSensitive = can(PERMISSIONS.EMPLOYEE_SENSITIVE_READ);
  const canEdit = employee ? can(PERMISSIONS.EMPLOYEE_UPDATE) : can(PERMISSIONS.EMPLOYEE_CREATE);

  useEffect(() => {
    form.resetFields();
    if (employee) {
      form.setFieldsValue({
        ...employee,
        birthDate: employee.birthDate ? dayjs(employee.birthDate) : undefined,
        hireDate: employee.hireDate ? dayjs(employee.hireDate) : undefined,
        dismissDate: employee.dismissDate ? dayjs(employee.dismissDate) : undefined,
      });
    }
  }, [employee, form]);

  const invalidate = [['employees'], ['employee'], ['drivers'], ['driver'], ['office-summary']];

  const save = useApiMutation(
    async (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        departmentId: values.departmentId ?? null,
        positionId: values.positionId ?? null,
        birthDate: toDate(values.birthDate),
        hireDate: toDate(values.hireDate),
        dismissDate: toDate(values.dismissDate),
      };
      if (employee) return (await api.patch<EmployeeDetail>(`/employees/${employee.id}`, payload)).data;
      return (await api.post<EmployeeDetail>('/employees', payload)).data;
    },
    {
      successMessage: employee ? t('Карточка обновлена') : t('Сотрудник принят'),
      invalidate,
    },
  );

  const uploadPhoto = useApiMutation(
    async (file: File) => (await employeePhoto.upload(employee!.id, file)).data,
    { successMessage: t('Фото загружено'), invalidate },
  );
  const removePhoto = useApiMutation(
    async () => (await employeePhoto.remove(employee!.id)).data,
    { successMessage: t('Фото удалено'), invalidate },
  );

  const photoSrc = useAuthedImage(employee?.photoKey ? `/employees/${employee.id}/photo` : null);
  const name = employee ? fullNameOf(employee) : undefined;

  const submit = (): void => {
    void form.validateFields().then((values) => {
      save.mutate(values, {
        onSuccess: (data) => {
          if (!employee) onCreated((data as EmployeeDetail).id);
        },
      });
    });
  };

  return (
    <>
      <SectionHeader
        title={t('Основные данные')}
        extra={
          canEdit && (
            <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} onClick={submit}>
              {employee ? t('Сохранить') : t('Принять сотрудника')}
            </Button>
          )
        }
      />
      {!employee && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('Остальные разделы карточки станут доступны после сохранения.')}
        />
      )}

      <div className="tms-employee-main">
        {/* Фото справа, поля обтекают его, как текст — снимок в газете:
            рядом с фото строки уже, под ним — снова на всю ширину. */}
        {employee && (
          <div className="tms-employee-photo">
            <div
              className="tms-employee-photo-frame"
              role={canEdit ? 'button' : undefined}
              tabIndex={canEdit ? 0 : undefined}
              onClick={canEdit ? () => photoInputRef.current?.click() : undefined}
              title={canEdit ? t('Загрузить или заменить фото') : undefined}
              style={{ cursor: canEdit ? 'pointer' : 'default' }}
            >
              {employee.photoKey && photoSrc ? (
                <img src={photoSrc} alt={t('Фото сотрудника')} />
              ) : (
                <div className="tms-employee-photo-empty" style={{ background: colorOf(name) }}>
                  {initials(name)}
                </div>
              )}
            </div>
            {canEdit && (
              <Space size={4}>
                <Button
                  size="small"
                  icon={<UploadOutlined />}
                  loading={uploadPhoto.isPending}
                  onClick={() => photoInputRef.current?.click()}
                >
                  {employee.photoKey ? t('Заменить') : t('Загрузить')}
                </Button>
                {employee.photoKey && (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={removePhoto.isPending}
                    onClick={() => removePhoto.mutate(undefined)}
                  />
                )}
              </Space>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) setSelectedFile(file);
                event.target.value = '';
              }}
            />
          </div>
        )}
        <Form form={form} layout="vertical" disabled={!canEdit}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="lastName" label={t('Фамилия')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="firstName" label={t('Имя')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="middleName" label={t('Отчество')}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="personnelNumber"
                label={t('Табельный номер')}
                rules={[{ required: true, message: t('Обязательное поле') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            {canSensitive && (
              <Col span={8}>
                <Form.Item name="birthDate" label={t('Дата рождения')}>
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
            )}
            <Col span={8}>
              <Form.Item name="workSchedule" label={t('Режим работы')}>
                <Select
                  allowClear
                  options={Object.values(WorkSchedule).map((value) => ({
                    value,
                    label: t(WORK_SCHEDULE_LABEL[value] ?? value),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="departmentId" label={t('Подразделение')}>
                <DepartmentSelect onChange={() => form.setFieldValue('positionId', undefined)} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="positionId"
                label={t('Должность')}
                tooltip={t('Если должность предполагает управление транспортом, сотрудник появится в «Водителях»')}
              >
                <Select
                  allowClear
                  disabled={!departmentId || !canEdit}
                  placeholder={departmentId ? undefined : t('Сначала выберите подразделение')}
                  options={(dictionaries.data?.positions ?? [])
                    .filter((position) => position.departmentId === departmentId)
                    .map((position) => ({ value: position.id, label: position.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="hireDate" label={t('Дата приёма')}>
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            {canSensitive && (
              <Col span={8}>
                <Form.Item name="hireOrderNumber" label={t('Номер приказа о приёме')}>
                  <Input />
                </Form.Item>
              </Col>
            )}
            {employee && (
              <>
                <Col span={8}>
                  <Form.Item
                    name="dismissDate"
                    label={t('Дата увольнения')}
                    tooltip={t('После заполнения сотрудник исчезнет из списков выдачи путевых листов и допусков')}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                  </Form.Item>
                </Col>
                {canSensitive && (
                  <Col span={8}>
                    <Form.Item name="dismissOrderNumber" label={t('Номер приказа об увольнении')}>
                      <Input />
                    </Form.Item>
                  </Col>
                )}
              </>
            )}
          </Row>
          {employee && (
            <Form.Item name="isActive" label={t('Работает')} valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
          <Form.Item name="notes" label={t('Примечание')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>

      </div>

      <PhotoCropModal
        open={selectedFile !== null}
        file={selectedFile}
        title={t('Фото сотрудника')}
        confirmLoading={uploadPhoto.isPending}
        onCancel={() => setSelectedFile(null)}
        onConfirm={(croppedFile) => {
          uploadPhoto.mutate(croppedFile, { onSuccess: () => setSelectedFile(null) });
        }}
      />
    </>
  );
}
